// runnerAPI.js
import axios from "axios";

/** ————— LocalStorage keys ————— */
const COMPANY_CODE_KEY = "company_code"; // بنخزنّه من شاشة اللوجين
const COMPANY_ID_KEY = "companyId"; // بيتخزن بعد اللوجين كـ flat field

/** ————— Helper: اقرأ companyID من اللوكال ————— */
const getLocalCompanyId = (fallback) => {
  try {
    const fromCode = (localStorage.getItem(COMPANY_CODE_KEY) || "").trim(); // الحقل اليدوي
    const fromId = (localStorage.getItem(COMPANY_ID_KEY) || "").trim(); // من saveRunnerData
    const v = fromCode || fromId || (fallback ? String(fallback).trim() : "");
    return v || "";
  } catch {
    return fallback ? String(fallback) : "";
  }
};

/** ————— Utils to persist runner data ————— */
export const saveRunnerData = (runnerData) => {
  try {
    localStorage.setItem("runnerData", JSON.stringify(runnerData));
    // flat fields for quick access
    if (runnerData?.data?.accessToken) {
      localStorage.setItem("accessToken", runnerData.data.accessToken);
    }
    if (runnerData?.data?.companyId ?? runnerData?.companyID) {
      const cid = runnerData.data?.companyId ?? runnerData.companyID;
      localStorage.setItem(COMPANY_ID_KEY, String(cid));
    }
  } catch (e) {
    console.warn("localStorage error:", e);
  }
};

export const loadRunnerData = () => {
  try {
    const raw = localStorage.getItem("runnerData");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearRunnerData = () => {
  try {
    localStorage.removeItem("runnerData");
    localStorage.removeItem("accessToken");
    localStorage.removeItem(COMPANY_ID_KEY);
  } catch {}
};

/** ————— Company config (optional) ————— */
export const getCompanyData = async (companyID) => {
  // استخدم قيمة من اللوكال أولاً ثم البراميتر ثم افتراضي
  const cid = getLocalCompanyId(companyID || "250804");
  try {
    const response = await fetch(
      "https://ahmedaglan.github.io/ClientsJSON/Clients.json"
    )
      .then((res) => res.json())
      .then((data) => {
        const founded = data.ClientsList.find(
          (company) => String(company.ClientID) === String(cid)
        );
        if (!founded) throw Error("cannot find companyID");
        return founded;
      });

    return {
      ...response,
      baseURL: `${response.APIHost}${response.APIPath}api/RunnerUsers/V${response?.ApiVersion}`,
    };
  } catch (err) {
    throw err;
  }
};

/** ————— Legacy login for RunnerUsers ————— */
export const handleLogin = async (baseURL, loginData, cb) => {
  const { username, password } = loginData || {};
  const companyID = getLocalCompanyId(loginData?.companyID || "250804");
  try {
    const res = await fetch(`${baseURL}/Login`, {
      method: "POST",
      body: JSON.stringify({ userName: username, password }),
      headers: {
        companyID: String(companyID),
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    const payload = await res.json();
    if (payload?.Message) throw new Error(payload.Message);

    const normalized = { ...payload, baseURL, companyID };
    cb?.(normalized);
    saveRunnerData(normalized);
    return normalized;
  } catch (err) {
    throw err;
  }
};

/** ————— New login (Users/V1) ————— */
export const handleRunnerData = () => ({
  login: async (formState, setRunnerData) => {
    try {
      // استخدم company_code/ companyId من اللوكال أولاً، ثم قيمة الفورم إن وجدت، ثم افتراضي
      const companyHeader = getLocalCompanyId(formState?.companyID || "250804");
      const baseURL = "https://vsoftopim2dev.com-eg.net/api/Users/V1";

      const response = await fetch(`${baseURL}/Login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          companyID: companyHeader,
        },
        body: JSON.stringify({
          username: formState?.username,
          password: formState?.password,
          roleID: formState?.roleID ?? 1,
        }),
      });

      if (!response.ok) {
        let errText = "";
        try {
          errText = await response.text();
        } catch {}
        throw new Error(
          `Login failed: HTTP ${response.status} ${errText || ""}`
        );
      }

      const payload = await response.json();
      if (!payload?.success)
        throw new Error(payload?.message || "Login failed");

      const normalizedRunnerData = {
        data: payload.data, // includes accessToken & companyId
        baseURL,
        companyID: payload.data?.companyId,
      };

      setRunnerData?.(normalizedRunnerData);
      saveRunnerData(normalizedRunnerData);

      return normalizedRunnerData;
    } catch (error) {
      console.error("Login error:", error);
      alert(error?.message || "Login failed. Please check your credentials.");
      throw error;
    }
  },
});

/** ————— Session/Confirm helpers ————— */
export const handleConfirm = async (runnerData, setRunnerData, shipments) => {
  const { baseURL, runnerID, accessToken, companyID } = runnerData || {};
  try {
    const response = await fetch(`${baseURL}/ConfirmAWBs/${runnerID}`, {
      method: "POST",
      body: JSON.stringify({ awBs: shipments }),
      headers: {
        companyID: String(companyID ?? getLocalCompanyId("250804")),
        accessToken: String(accessToken),
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    await handleLogout(
      baseURL,
      setRunnerData,
      accessToken,
      runnerID,
      companyID
    );
    return response;
  } catch (err) {
    console.error(err);
    await handleLogout(
      baseURL,
      setRunnerData,
      runnerID ? accessToken : "",
      runnerID || "",
      companyID || getLocalCompanyId("250804")
    );
  }
};

export const ConfirmPickupShipments = async (
  runnerData,
  setRunnerData,
  shipments
) => {
  const { baseURL, runnerID, accessToken, companyID } = runnerData || {};
  try {
    const response = await axios.post(
      `${baseURL}/ConfirmPickupShipments/${runnerID}`,
      shipments,
      {
        headers: {
          companyID: String(companyID ?? getLocalCompanyId("250804")),
          accessToken: String(accessToken),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    await handleLogout(
      baseURL,
      setRunnerData,
      accessToken,
      runnerID,
      companyID
    );
    return response;
  } catch (err) {
    console.error(err);
    await handleLogout(
      baseURL,
      setRunnerData,
      runnerID ? accessToken : "",
      runnerID || "",
      companyID || getLocalCompanyId("250804")
    );
  }
};

export const handleCheck = async (runnerData, setRunnerData) => {
  const { baseURL, runnerID, accessToken, companyID } = runnerData || {};
  try {
    const response = await fetch(`${baseURL}/ConfirmAWBs/${runnerID}`, {
      method: "POST",
      body: JSON.stringify({ awBs: [] }),
      headers: {
        companyID: String(companyID ?? getLocalCompanyId("250804")),
        accessToken: String(accessToken),
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.status === 401) {
      setRunnerData?.(null);
      clearRunnerData();
    }
    return response;
  } catch (err) {
    console.error(err);
  }
};

export const handleLogout = async (
  baseURL,
  cb,
  accessToken,
  runnerID,
  companyID
) => {
  try {
    await fetch(`${baseURL}/Logout/${runnerID}`, {
      method: "POST",
      body: "{}",
      headers: {
        companyID: String(companyID ?? getLocalCompanyId("250804")),
        Accept: "application/json",
        "Content-Type": "application/json",
        accessToken: String(accessToken),
      },
    });
    cb?.(null);
    clearRunnerData();
  } catch (err) {
    console.error("Logout error:", err);
  }
};
